// SCR-STAFF-711 (AP-MED-04؛ US-MED-088، US-CLN-160): ویزیت «فقط نسخه» در یک صفحه فشرده — سربرگ هشدار بالینی، وضعیت
// استحقاق، ویرایشگر نسخه (COMP-RX-EDITOR با Ctrl+Enter)، ویرایشگر درخواست (COMP-ORDER-EDITOR) و «بعدی» که مواجهه را
// می‌بندد و نفر بعد CheckIn‌شده را باز می‌کند.
//
// سطح مستندسازی مواجهه هنگام شروع `RxOnly` است (US-CLN-160)، پس یادداشت الزامی نیست و پایان مواجهه دست‌کم یک نسخه یا
// درخواست صادرشده یا دلیل «بدون نسخه» می‌خواهد (BR-CLN-150؛ PHX-CLN-0309). ارتقای سطح همیشه پیش از پایان مجاز است.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type ClnDocumentationLevel } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog, ListPage } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { PatientCoveragePanel } from '../insurance/PatientCoveragePanel';
import { OrderEditor } from './OrderEditor';
import { RxEditor } from './RxEditor';
import { SCR_STAFF_711 } from './highVolume';

const LEVELS: readonly NonNullable<ClnDocumentationLevel>[] = ['RxOnly', 'Simple', 'Full'];

export function RxOnlyVisitPage() {
  const { t } = useTranslation(['med', 'cln']);
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const encounterId = params.get('encounterId') ?? '';
  const [dialog, setDialog] = useState<'rx' | 'order' | 'reason' | null>(null);
  const [reason, setReason] = useState('');
  const tenantId = session?.tenantId;

  const encounter = useQuery({
    queryKey: ['clinical', tenantId, 'encounter', encounterId],
    enabled: Boolean(tenantId) && encounterId.length > 0,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/encounters/{id}', { params: { path: { id: encounterId } } })),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['clinical', tenantId, 'encounter', encounterId] });

  const upgrade = useMutation({
    mutationFn: (level: NonNullable<ClnDocumentationLevel>) =>
      unwrap(tenantApi.POST('/api/v1/clinical/encounters/{id}:documentation-level', { params: { path: { id: encounterId } }, body: { level } })),
    onSuccess: refresh,
  });

  const noPrescription = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/clinical/encounters/{id}:no-prescription-reason', { params: { path: { id: encounterId } }, body: { reason } })),
    onSuccess: () => {
      setDialog(null);
      setReason('');
      refresh();
    },
  });

  const next = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/encounters/{id}:complete', {
          params: { path: { id: encounterId } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (view) => {
      refresh();

      // AC-MED-088.2: «بعدی» نفر بعد CheckIn‌شده همان پزشک را باز می‌کند؛ نبود نفر بعد به میز کار برمی‌گردد.
      void navigate(view.nextAppointmentId ? `/desk?appointmentId=${view.nextAppointmentId}` : '/desk');
    },
  });

  const view = encounter.data;
  const completionError = isApiError(next.error) ? next.error.code : undefined;

  if (!encounterId) {
    return (
      <ListPage screenId={SCR_STAFF_711} titleKey="med:rx_only.title">
        <Alert severity="info" data-testid="med-rx-only-no-encounter">
          {t('med:rx_only.pick_encounter')}
        </Alert>
      </ListPage>
    );
  }

  return (
    <ListPage screenId={SCR_STAFF_711} titleKey="med:rx_only.title" subtitleKey="med:rx_only.subtitle">
      <Stack spacing={2} data-testid="med-rx-only">
        {encounter.error ? <ErrorState error={encounter.error} onRetry={() => void encounter.refetch()} /> : null}
        {view ? (
          <>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="h6">{view.patientName ?? '—'}</Typography>
              {view.fileNumber ? <Chip size="small" label={view.fileNumber} /> : null}
              <Chip size="small" color="info" label={t(`cln:documentation_level.${view.documentationLevel}`)} data-testid="med-rx-only-level" />
              {view.noPrescriptionReason ? <Chip size="small" variant="outlined" label={t('med:rx_only.no_prescription_recorded')} /> : null}
            </Stack>
            {(view.warnings ?? []).length > 0 ? (
              <Alert severity="warning" data-testid="med-rx-only-alerts">
                {(view.warnings ?? []).map((warning) => warning.message).join(t('common:list_separator'))}
              </Alert>
            ) : null}
            {completionError === 'PHX-CLN-0309' ? (
              <Alert severity="warning" data-testid="med-rx-only-outcome-required">
                {t('med:rx_only.outcome_required')}
              </Alert>
            ) : null}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button variant="contained" onClick={() => setDialog('rx')} data-testid="med-rx-only-prescription">
                {t('med:rx_only.prescription')}
              </Button>
              <Button variant="outlined" onClick={() => setDialog('order')} data-testid="med-rx-only-order">
                {t('med:rx_only.order')}
              </Button>
              <Button variant="text" onClick={() => setDialog('reason')} data-testid="med-rx-only-reason">
                {t('med:rx_only.no_prescription')}
              </Button>
              <TextField
                select
                size="small"
                label={t('cln:documentation_level.label')}
                value={view.documentationLevel}
                onChange={(event) => upgrade.mutate(event.target.value as NonNullable<ClnDocumentationLevel>)}
                slotProps={{ htmlInput: { 'data-testid': 'med-rx-only-upgrade' } }}
                sx={{ minWidth: 180 }}
                disabled={!access.can('cln.encounter.manage')}
              >
                {LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {t(`cln:documentation_level.${level}`)}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" color="secondary" onClick={() => next.mutate()} disabled={next.isPending} data-testid="med-rx-only-next">
                {t('med:rx_only.next')}
              </Button>
            </Stack>
            {upgrade.error ? <ErrorState error={upgrade.error} compact /> : null}
            {/* AC-MED-088.3: وضعیت استحقاق پوشش‌های فعال با دکمه «به‌روزرسانی استحقاق» (BR-INS-030: نتیجه معتبر
                همان روز دوباره استعلام نمی‌شود). */}
            <PatientCoveragePanel patientId={view.patientId} />
            {dialog === 'rx' ? <RxEditor open encounterId={view.id} patientId={view.patientId} onClose={() => setDialog(null)} onChanged={refresh} /> : null}
            {dialog === 'order' ? <OrderEditor open encounterId={view.id} patientId={view.patientId} onClose={() => setDialog(null)} /> : null}
            {dialog === 'reason' ? (
              <FormDialog
                open
                titleKey="med:rx_only.no_prescription"
                submitKey="med:rx_only.no_prescription"
                screenId={SCR_STAFF_711}
                onSubmit={() => (reason.trim() ? noPrescription.mutate() : undefined)}
                onClose={() => setDialog(null)}
                isDirty={reason.trim().length > 0}
                isSubmitting={noPrescription.isPending}
                submitError={noPrescription.error}
              >
                <TextField
                  label={t('med:rx_only.no_prescription_reason')}
                  value={reason}
                  multiline
                  minRows={2}
                  onChange={(event) => setReason(event.target.value)}
                  slotProps={{ htmlInput: { maxLength: 300, 'data-testid': 'med-rx-only-reason-text' } }}
                />
              </FormDialog>
            ) : null}
          </>
        ) : null}
      </Stack>
    </ListPage>
  );
}
